import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useTagDetail, useTagGroupDetail, useTagGroups, useTagUpdate } from '@/hooks';
import { ArrowLeft, Building, Layers, Lock } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { AppLink } from '@/components/ui/app-link';
import { toast } from 'sonner';
import { getAxiosErrorMessage } from '@/utils/error-utils';
import { ownerScopeToInputScope } from '@/lib/owner-scope';
import { TagFormValues, Work4MeTagForm } from './Work4MeTagForm';

export function Work4MeTagDetail() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const parsedTagId = Number(id);
  const isValidTagId = Number.isInteger(parsedTagId) && parsedTagId > 0;
  const detailQuery = useTagDetail(isValidTagId ? parsedTagId : 0);
  const tag = detailQuery.data;
  const tagGroupQuery = useTagGroupDetail(tag?.tagGroupId ?? 0);
  const tagGroup = tagGroupQuery.data;
  const tagGroupsQuery = useTagGroups();
  const updateTagMutation = useTagUpdate();

  const initialValues: TagFormValues | null = tag
    ? {
      name: tag.name,
      description: tag.description ?? '',
      scope: ownerScopeToInputScope(tag.ownerScope),
      tagGroupId: tag.tagGroupId ?? null,
      tagOrder: tag.tagOrder ?? undefined,
    }
    : null;

  if (!isValidTagId) {
    return (
      <div className="rounded-lg border bg-card p-5 m-4">
        <p className="text-sm text-destructive">Invalid tag id.</p>
      </div>
    );
  }

  if (detailQuery.isLoading) {
    return (
      <div className="rounded-lg border bg-card p-5 m-4">
        <p className="text-sm text-muted-foreground">Loading tag...</p>
      </div>
    );
  }

  if (detailQuery.isError || !tag) {
    return (
      <div className="rounded-lg border bg-card p-5 m-4">
        <p className="text-sm text-destructive">Unable to load tag detail.</p>
      </div>
    );
  }

  return (
    <div className="-mx-4 py-2">
      <div className="px-4 py-3 border-b bg-muted/20">
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-4">
            <h2 className="text-xl font-light tracking-wide">Tag: {tag.name}</h2>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <Badge variant="outline">{tag.ownerScope}</Badge>
              <Button type="button" variant="outline" size="sm" onClick={() => navigate('/work4me/tags')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Tags
              </Button>
            </div>
          </div>
        </div>
      </div>

      {initialValues ? (
        <div className="px-4 pt-4 pb-2">
          <Work4MeTagForm
            initialValues={initialValues}
            tagGroups={tagGroupsQuery.data ?? []}
            isSubmitting={updateTagMutation.isPending}
            submitLabel="Save Tag"
            submittingLabel="Saving..."
            showScopeSelector={false}
            onSubmit={async (values) => {
              await updateTagMutation.mutateAsync(
                {
                  id: tag.id,
                  data: {
                    name: values.name,
                    description: values.description === '' ? null : values.description,
                    tagGroupId: values.tagGroupId,
                    tagOrder: values.tagOrder,
                  },
                },
                {
                  onSuccess: () => {
                    toast.success(`Saved "${values.name}".`);
                  },
                  onError: (error) => {
                    toast.error(getAxiosErrorMessage(error) ?? 'Failed to update tag.');
                  },
                }
              );
            }}
          />
          <div className="grid gap-3 sm:grid-cols-2 pt-4">
            <div className="rounded-md border p-3">
              <p className="text-xs upperanchor tracking-wide text-muted-foreground">Scope</p>
              <p className="mt-1 text-sm inline-flex items-center gap-1">
                {tag.ownerScope === 'HUB' ? <Building className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
                {tag.ownerScope}
              </p>
            </div>
            <div className="rounded-md border p-3">
              <p className="text-xs upperanchor tracking-wide text-muted-foreground">Tag Group</p>
              {tag.tagGroupId ? (
                <div className="mt-1">
                  <AppLink
                    to={`/work4me/tag-groups/${tag.tagGroupId}`}
                    className="text-sm inline-flex items-center gap-1 text-primary hover:underline"
                  >
                    <Layers className="h-4 w-4" />
                    {tagGroup?.name?.trim() || `Group ${tag.tagGroupId}`}
                  </AppLink>
                </div>
              ) : (
                <p className="mt-1 text-sm text-muted-foreground">Ungrouped</p>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
